import React, { memo, useState, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useSelector } from "react-redux";
import { useAuth } from "../../contexts/JWTContext";
import styles from "./Footer.module.css";
import LoginModal from "../Modals/LoginModal";

const Footer = memo(({ onDepositClick, onContactClick, onMeClick }) => {
  const { logout, isAuthenticated } = useAuth();
  const [isLoginModalOpen, setLoginModalOpen] = useState(false);
  const navigate = useNavigate();
  const websiteSetting = useSelector((state) => state.user.websiteSetting);
  const settings = Array.isArray(websiteSetting)
    ? websiteSetting[0]
    : websiteSetting?.data?.[0] || {};
  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(
    /\/$/,
    "",
  );

  const openLogin = useCallback(() => setLoginModalOpen(true), []);
  const closeLogin = useCallback(() => setLoginModalOpen(false), []);

  const handleDepositClick = useCallback(() => {
    if (!isAuthenticated) {
      openLogin();
      return;
    }
    navigate("/menu?tab=deposit");
  }, [isAuthenticated, navigate, openLogin]);

  const handleLogout = () => {
    try {
      // Ensure auth context logs out first
      logout();
    } catch (error) {
      console.error("Logout (auth) error:", error);
    }

    try {
      // Remove common keys then fully clear storage to avoid leftover data
      localStorage.removeItem("user");
      localStorage.removeItem("userId");
      localStorage.removeItem("token");
      localStorage.removeItem("authToken");
      localStorage.removeItem("persist:root");
      // Clear everything to be safe
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      // ignore storage errors
    }

    // Close any open modals
    setLoginModalOpen(false);

    // Redirect to home
    window.location.href = "/";
  };

  const resolveIconSrc = (path) => {
    if (!path) return "";
    if (/^https?:/i.test(path)) return path;
    return `${apiBaseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
  };

  const renderFooterIcon = (customPath, fallbackIcon, alt) => {
    const src = resolveIconSrc(customPath);
    if (src) {
      return <img src={src} alt={alt} className={styles.iconImage} />;
    }
    return <Icon icon={fallbackIcon} className={styles.icon} />;
  };

  return (
    <>
      <footer className={styles.footer}>
        <div className={styles.container}>
          <button
            className={styles.footerButton}
            onClick={handleDepositClick}
            title="ฝาก-ถอน"
          >
            {renderFooterIcon(
              settings?.headerGameIconUrl,
              "material-symbols:account-balance-wallet",
              "ฝาก-ถอน",
            )}
            <span className={styles.text}>ฝาก-ถอน</span>
          </button>
          <NavLink
            to="/promotion"
            className={({ isActive }) =>
              `${styles.footerButton} ${isActive ? styles.active : ""}`
            }
            title="โปรโมชั่น"
          >
            {renderFooterIcon(
              settings?.headerPromotionIconUrl,
              "material-symbols:featured-seasonal-and-gifts-rounded",
              "โปรโมชั่น",
            )}
            <span className={styles.text}>โปรโมชั่น</span>
          </NavLink>
          <NavLink
            to="/"
            className={({ isActive }) =>
              `${styles.footerButton} ${isActive ? styles.active : ""}`
            }
            title="หน้าหลัก"
          >
            {renderFooterIcon(
              settings?.footerHomeIconUrl,
              "material-symbols:home",
              "หน้าหลัก",
            )}
            <span className={styles.text}>หน้าหลัก</span>
          </NavLink>

          <button
            className={styles.footerButton}
            onClick={onContactClick}
            title="ติดต่อเรา"
          >
            {renderFooterIcon(
              settings?.headerContactIconUrl,
              "material-symbols:contact-support",
              "ติดต่อเรา",
            )}
            <span className={styles.text}>ติดต่อเรา</span>
          </button>
          {!isAuthenticated && (
            <button
              className={styles.footerButton}
              onClick={openLogin}
              title="เข้าสู่ระบบ"
            >
              {renderFooterIcon(
                settings?.footerLoginIconUrl,
                "material-symbols:login",
                "เข้าสู่ระบบ",
              )}
              <span className={styles.text}>เข้าสู่ระบบ</span>
            </button>
          )}
          {isAuthenticated && (
            <button
              className={styles.footerButton}
              onClick={handleLogout}
              title="ออกจากระบบ"
            >
              {renderFooterIcon(
                settings?.footerLogoutIconUrl,
                "mingcute:exit-line",
                "ออกจากระบบ",
              )}
              <span className={styles.text}>ออก</span>
            </button>
          )}
        </div>
      </footer>
      {!isAuthenticated && (
        <LoginModal isOpen={isLoginModalOpen} onClose={closeLogin} />
      )}
    </>
  );
});

Footer.displayName = "Footer";

export default Footer;
